import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../lib/api";
import { runActive, runsApi, type RunEvent, type RunInfo } from "../../lib/productsApi";

const POLL_MS = 2500;
const PAGE = 200;

/**
 * Watch a run: its state and its log, polled while it is active (there is no push channel yet).
 * `onEnd` runs once when the run is over, after its whole log has been read.
 */
export function useRunWatch(runId: string | null, onEnd?: (run: RunInfo) => void) {
  const [run, setRun] = useState<RunInfo | null>(null);
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef(onEnd);
  endRef.current = onEnd;

  useEffect(() => {
    if (!runId) return;
    let alive = true;
    let after = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setRun(null);
    setEvents([]);
    setError(null);
    const tick = async () => {
      try {
        const [r, ev] = await Promise.all([runsApi.get(runId), runsApi.events(runId, after)]);
        if (!alive) return;
        setError(null);
        if (ev.events.length) {
          after = ev.events[ev.events.length - 1].seq;
          setEvents((prev) => [...prev, ...ev.events]);
        }
        setRun(r.run);
        if (ev.events.length === PAGE) {
          timer = setTimeout(tick, 0);
          return;
        }
        if (!runActive(r.run.state)) {
          endRef.current?.(r.run);
          return;
        }
      } catch (e) {
        if (!alive) return;
        setError(e instanceof ApiError ? e.message : String(e));
      }
      timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [runId]);

  return { run, events, error };
}
